import React, { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useRealtimeDrivers } from 'Core/hooks';
import moment from "moment";
import { useIntl } from "react-intl";

//Controls
import { Tooltip, Icons } from "@gpstrackit/gtc-react-controls";
import {
    Form,
    Select,
    Input,
    Switch,
    InputNumeric
} from "@gpstrackit/gtc-react-controls";
import { Grid, Box } from "@mui/material";
import { Slider } from "Components";

// Permissions
import { HasPermission } from "Core/security"

// Views
import VehicleStatusIconPicker from "Modules/settings/views/unitSettings/components/vehicleStatusIconPicker"

// Helpers
import { cleanString } from 'Core/data/Helpers';

const MAX_VALUE = 99999999.99

const roundToTwoDecimals = (value) => Math.round((value || 0) * 100) / 100;

const Unit = ({
    formData,
    setFormData,
    udpateVehicleStatusIcon,
    setUpdateVehicleStatusIcon,
    onEcuChange,
    enableAll,
    setEnableAll,
    unitFormRef
}) => {
    const { messages, locale } = useIntl();
    //Redux selectors
    const unit = useSelector((state) => state.fleetModalUpdateUnitRedux.unit);
    const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });
    const user = useSelector((state) => state.securityRedux.user);
    const locations = useSelector(state => state.catalogsRedux.locations);
    const timeZones = useSelector(state => state.catalogsRedux.timeZones);
    const vehicleStatus = useSelector(state => state.catalogsRedux.vehicleStatus);
    const apolloEldPermission = HasPermission(user, 'apolloeld');

    const itemRefs = useRef([]);

    const registerItem = useCallback((el) => {
        if (!el) return;
        if (!itemRefs.current.includes(el)) itemRefs.current.push(el);
    }, []);

    const didWrapSubmit = useRef(false);

    useEffect(() => {
        if (didWrapSubmit.current) return;
        if (!unitFormRef || typeof unitFormRef.submit !== "function") return;

        didWrapSubmit.current = true;

        const originalSubmit = unitFormRef.submit;

        unitFormRef.submit = (...args) => {
            itemRefs?.current.forEach((r) => r?.validate && r.validate());
            return originalSubmit(...args);
        };

        return () => {
            unitFormRef.submit = originalSubmit;
            didWrapSubmit.current = false;
        };
    }, [unitFormRef]);

    const filterIcon = (unit) => {
        let vehicleStatusId = unit?.vehicleStatusId || unit.lastReading?.vehicleStatusId;
        return vehicleStatus?.find(x => x.id == vehicleStatusId)
    }

    let icon;

    if (!_.isEmpty(formData)) {
        icon = filterIcon(formData);
    }

    return (
        <>
            <Grid
                container
                sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Grid item xs={12}>
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 5
                    }}>
                        <div>
                            <span className="required-gtc-rc">*</span>
                            <label className="label-gtc-rc">{messages['fleetModalUpdateUnit_unitLabelAndIcon']}</label>
                        </div>
                        <div>
                            <Switch
                                id="customIcon"
                                optionLabels={[messages['fleetModalUpdateUnit_custom'], messages['fleetModalUpdateUnit_custom']]}
                                size={locale === 'es' ? "xlarge" : "large"}
                                onChange={(checked) => {
                                    setFormData({
                                        ...formData,
                                        vehicleStatusId: checked ? filterIcon(unit)?.id : 0,
                                    });
                                    setUpdateVehicleStatusIcon(checked);
                                }}
                                checked={udpateVehicleStatusIcon}
                            />
                        </div>
                    </div>
                </Grid>
                <Grid item xs={1} alignSelf={'self-start'} mt={'5px'} >
                    <div>
                        <VehicleStatusIconPicker
                            icon={icon?.icon}
                            icons={vehicleStatus}
                            disabled={!udpateVehicleStatusIcon}
                            loading={false}
                            vehicleStatusId={formData?.vehicleStatusId}
                            onChange={(value) => {
                                setFormData({
                                    ...formData,
                                    vehicleStatusId: value?.id,
                                });
                            }}
                        />
                    </div>
                </Grid>
                <Grid item xs={11} className="unit-settings-label-input">
                    <Form.Item
                        ref={registerItem}
                        name="label"
                        rules={[
                            { required: true, message: messages["required_field"] },
                        ]}
                    >
                        <Input
                            id="label"
                            type="text"
                            placeholder={messages["fleetModalUpdateUnit_unitLabelAndIcon"]}
                            onChange={(value) => {
                                const cleanedValue = cleanString(value);
                                setFormData({ ...formData, label: cleanedValue });
                                if (cleanedValue !== value) {
                                    setTimeout(() => {
                                        unitFormRef.setValues({ label: cleanedValue });
                                    }, 100);
                                }
                            }}
                            maxLength={50}
                        />
                    </Form.Item>
                    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', marginTop: -8 }}>
                        <span style={{ fontSize: 12, color: '#666' }}>
                            {String(formData?.label ?? '').length} / 50
                        </span>
                    </Box>
                </Grid>
            </Grid>
            {
                unit?.jbus || unit?.vbus ? (
                    <div className="unit-settings-item-switch">
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 20
                        }}>
                            <label className="label-gtc-rc">ECU</label>
                            <div>
                                <a
                                    style={{ color: '#4688EB', cursor: 'pointer', textDecoration: 'underline' }}
                                    onClick={() => {
                                        let newValue = !enableAll;
                                        setFormData({
                                            ...formData,
                                            useEcuEngineHours: newValue,
                                            useEcuOdometer: newValue,
                                            useEcuVin: newValue,
                                            useEcuSpeed: newValue,
                                        });
                                        setEnableAll(newValue);
                                    }}
                                >
                                    {enableAll ? messages['fleetModalUpdateUnit_disabled'] : messages['fleetModalUpdateUnit_enable']}
                                </a>
                            </div>
                        </div>
                    </div>
                ) : <></>
            }
            {
                unit?.unitVersion?.odometerEnable &&
                <div style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <label className="label-gtc-rc">{messages['fleetModalUpdateUnit_speed']}</label>
                        {
                            (formData.jbus || formData.vbus) ?
                                <div className="unit-settings-unit-switch">
                                    <Switch
                                        id="useEcuSpeed"
                                        optionLabels={["ECU", "ECU"]}
                                        size={"medium"}
                                        checked={Boolean(formData.useEcuSpeed) || false}
                                        onChange={(value) => {
                                            onEcuChange(value, "useEcuSpeed")
                                        }}
                                    />
                                </div>
                                : <></>
                        }
                    </div>
                    <span className="subtitle">
                        {formData.useEcuSpeed
                            ? messages["fleetModalUpdateUnit_calculatedByEcu"]
                            : messages["fleetModalUpdateUnit_calculatedByGps"]}
                    </span>
                </div>
            }

            {
                unit?.unitVersion?.odometerEnable &&
                <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <label className="label-gtc-rc">{messages['fleetModalUpdateUnit_odometer']}</label>
                        {
                            (formData.jbus || formData.vbus) ?
                                <div className="unit-settings-unit-switch">
                                    <Switch
                                        id="useEcuOdometer"
                                        optionLabels={["ECU", "ECU"]}
                                        size={"medium"}
                                        checked={Boolean(formData.useEcuOdometer) || false}
                                        isDisabled={!formData.jbus && !formData.vbus}
                                        onChange={(value) => {
                                            onEcuChange(value, "useEcuOdometer")
                                        }}
                                    />
                                </div> : <></>
                        }
                    </div>
                    <div className="unit-settings-unit-switch">
                        <Form.Item
                            ref={registerItem}
                            name="dashOdometer"
                            label={`${messages['fleetModalUpdateUnit_dashReading']} (${user.isKph ? "km" : "mi"})`}
                        >
                            <InputNumeric
                                id="dashOdometer"
                                type="number"
                                decimalPlaces={2}
                                placeholder={`${messages['fleetModalUpdateUnit_dashReading']} (${user.isKph ? "km" : "mi"})`}
                                onChange={(value) => {
                                    const isEmpty = value === '' || value === null || value === undefined;

                                    if (isEmpty) {
                                        setFormData({
                                            ...formData,
                                            dashOdometer: null,
                                            offsetEcuEngineOdometer: null,
                                            offsetEngineOdometer: null,
                                            offsetEngineOdometerUpdatedOn: null,
                                            offsetEcuEngineOdometerUpdatedOn: null,
                                        });

                                        setTimeout(() => {
                                            unitFormRef.setValues({ dashOdometer: null });
                                        }, 0);

                                        return;
                                    }

                                    const stringValue = String(value ?? '');
                                    const [integerPart, decimalPart] = stringValue.split('.');

                                    if (integerPart && integerPart.replace('-', '').length > 8) {
                                        const truncatedValue = decimalPart
                                            ? parseFloat(`${integerPart.slice(0, 8)}.${decimalPart}`)
                                            : parseFloat(integerPart.slice(0, 8));
                                        const normalized = Math.abs(truncatedValue);
                                        setTimeout(() => {
                                            unitFormRef.setValues({ dashOdometer: normalized });
                                        }, 50);
                                        return;
                                    }

                                    let currentValue = Number(value);

                                    if (currentValue < 0) {
                                        currentValue = Math.abs(currentValue);
                                        setTimeout(() => {
                                            unitFormRef.setValues({ dashOdometer: currentValue });
                                        }, 100);
                                    }

                                    if (currentValue > MAX_VALUE) {
                                        currentValue = MAX_VALUE;
                                        setTimeout(() => {
                                            unitFormRef.setValues({ dashOdometer: currentValue });
                                        }, 100);
                                    }

                                    const ecuOdometer = Number(formData.ecuEngineOdometer) || 0;
                                    const engineOdometer = Number(formData.engineOdometer) || 0;

                                    const newOffsetEcu = currentValue - ecuOdometer;
                                    const newOffsetEngine = currentValue - engineOdometer;

                                    setFormData({
                                        ...formData,
                                        dashOdometer: currentValue,
                                        offsetEcuEngineOdometer: newOffsetEcu,
                                        offsetEngineOdometer: newOffsetEngine,
                                        offsetEngineOdometerUpdatedOn:
                                            !formData.useEcuOdometer
                                                ? moment().utc().format()
                                                : formData.offsetEngineOdometerUpdatedOn ||
                                                null,
                                        offsetEcuEngineOdometerUpdatedOn:
                                            formData.useEcuOdometer
                                                ? moment().utc().format()
                                                : formData.offsetEcuEngineOdometerUpdatedOn ||
                                                null,
                                    });
                                }}
                                maxLength={11}
                            />
                        </Form.Item>
                    </div>
                    <div >
                        <span className="subtitle" style={{ marginRight: 1 }}>
                            {`${formData?.useEcuOdometer ? "ECU:" : "GPS:"} ${formData.useEcuOdometer
                                ? `${Number(formData.ecuEngineOdometer || 0).toFixed(2)} (+ ${Number(formData.offsetEcuEngineOdometer || 0).toFixed(2)})`
                                : `${Number(formData.engineOdometer || 0).toFixed(2)} (+ ${Number(formData.offsetEngineOdometer || 0).toFixed(2)})`
                            }`}
                        </span>
                        <span className="subtitle">
                            {formData?.offsetEngineOdometerUpdatedOnLabel ?
                                `/ ${messages["fleetModalUpdateUnit_lastUpdated"]}`
                                + " " +
                                formData?.offsetEngineOdometerUpdatedOnLabel : null}
                        </span>
                    </div>
                </>
            }

            {
                unit?.deviceModel?.hoursEnable &&
                <>
                    <div style={{ marginTop: 10 }}>
                        <label className="label-gtc-rc">{messages['fleetModalUpdateUnit_engineHours']}</label>
                        {
                            (formData.jbus || formData.vbus) ?
                                <Switch
                                    id="engineHoursValue"
                                    optionLabels={["ECU", "ECU"]}
                                    size={"medium"}
                                    checked={Boolean(formData.useEcuEngineHours) || false}
                                    isDisabled={!formData.jbus && !formData.vbus}
                                    onChange={(checked) => onEcuChange(checked, "useEcuEngineHours")}
                                /> : <></>
                        }
                    </div>
                    <div className="unit-settings-unit-switch">
                        <Form.Item
                            ref={registerItem}
                            name="dashEngineHours"
                            label={messages['fleetModalUpdateUnit_actual']}
                        >
                            <InputNumeric
                                id="dashEngineHours"
                                type="number"
                                placeholder={messages['fleetModalUpdateUnit_actual']}
                                onChange={(value) => {
                                    const isEmpty = value === '' || value === null || value === undefined;

                                    if (isEmpty) {
                                        setFormData({
                                            ...formData,
                                            dashEngineHours: null,
                                            offsetEcuEngineHours: null,
                                            offsetEngineHours: null,
                                            offsetEngineHoursUpdatedOn: null,
                                            offsetEcuEngineHoursUpdatedOn: null,
                                        });

                                        setTimeout(() => {
                                            unitFormRef.setValues({ dashEngineHours: null });
                                        }, 0);

                                        return;
                                    }

                                    const stringValue = String(value ?? '');
                                    const [integerPart, decimalPart] = stringValue.split('.');
                                    
                                    if (integerPart && integerPart.replace('-', '').length > 8) {
                                        const truncatedValue = decimalPart 
                                            ? parseFloat(`${integerPart.slice(0, 8)}.${decimalPart}`)
                                            : parseFloat(integerPart.slice(0, 8));
                                        const normalized = Math.abs(truncatedValue);
                                        setTimeout(() => {
                                            unitFormRef.setValues({ dashEngineHours: normalized });
                                        }, 100);
                                        return;
                                    }
                                    
                                    let currentValue = Number(value);
                                    
                                    const isNegative = currentValue < 0;
                                    if (isNegative) {
                                        currentValue = Math.abs(currentValue);
                                        setTimeout(() => {
                                            unitFormRef.setValues({ dashEngineHours: currentValue });
                                        }, 100);
                                    }

                                    if (currentValue > MAX_VALUE) {
                                        currentValue = MAX_VALUE;
                                        setTimeout(() => {
                                            unitFormRef.setValues({ dashEngineHours: currentValue });
                                        }, 50);
                                    }
                                    
                                    const ecuEngineHours = Number(formData.ecuEngineHours) || 0;
                                    const engineHours = Number(formData.engineHours) || 0;
                                    
                                    setFormData({
                                        ...formData,
                                        dashEngineHours: currentValue,
                                        offsetEcuEngineHours: currentValue - ecuEngineHours,
                                        offsetEngineHours: currentValue - engineHours,
                                        offsetEngineHoursUpdatedOn:
                                            !formData.useEcuEngineHours
                                                ? moment().utc().format()
                                                : formData.offsetEngineHoursUpdatedOn ||
                                                null,
                                        offsetEcuEngineHoursUpdatedOn:
                                            formData.useEcuEngineHours
                                                ? moment().utc().format()
                                                : formData.offsetEcuEngineHoursUpdatedOn ||
                                                null,
                                    });
                                }}
                                maxLength={11}
                                decimalPlaces={2}
                                max={MAX_VALUE}
                            />
                        </Form.Item>
                    </div>
                    <div>
                        <span className="subtitle">
                            {`${formData.useEcuEngineHours ? "ECU:" : "GPS:"}
                                                                ${formData?.useEcuEngineHours
                                    ? `${roundToTwoDecimals(formData?.ecuEngineHours)} (+ ${roundToTwoDecimals(formData?.offsetEcuEngineHours)})`
                                    : `${roundToTwoDecimals(formData?.engineHours)} (+ ${roundToTwoDecimals(formData?.offsetEngineHours)})`
                                }`}
                        </span>
                        {formData?.offsetEngineHoursUpdatedOnLabel ?
                            <span className="subtitle">
                                {`/ ${messages["fleetModalUpdateUnit_lastUpdated"]} ${formData?.offsetEngineHoursUpdatedOnLabel}`}
                            </span>
                            : <></>
                        }
                    </div>
                </>
            }
            <div style={{ marginTop: 5 }}>
                <Form.Item
                    ref={registerItem}
                    name="driver"
                    label={messages['fleetModalUpdateUnit_assignedDriver']}
                >
                    <Select
                        id="driver"
                        options={drivers || []}
                        placeholder={messages['select']}
                        valueField="driverId"
                        textField="name"
                        style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                        isSearchable
                        isClearable
                        onChange={(value) => {
                            setFormData({
                                ...formData,
                                driver: value,
                                driverId: value?.driverId || 0,
                            });
                        }}
                    />
                </Form.Item>
            </div>
            {
                /* Default Driver with permision */
                HasPermission(user, "defDrvr", [290]) &&
                <Grid
                    container
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Grid item xs={11}>
                        <Form.Item
                            ref={registerItem}
                            name="defaultDriver"
                            label={messages['fleetModalUpdateUnit_defaultDriver']}
                        >
                            <Select
                                id="defaultDriver"
                                options={drivers || []}
                                placeholder={messages['select']}
                                valueField="driverId"
                                textField="name"
                                style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                                isSearchable
                                isClearable
                                onChange={(value) => {
                                    setFormData({
                                        ...formData,
                                        defaultDriver: value,
                                        defaultDriverId: value?.driverId || 0,
                                    });
                                }}
                            />
                        </Form.Item>
                    </Grid>
                    <Grid item xs={1}>
                        <Tooltip
                            placement="bottom"
                            title={messages['fleetModalUpdateUnit_driverDefaultInfo']}
                            style={{ marginTop: 12, marginLeft: 5 }}
                        >
                            <Icons
                                name="help"
                                size="medium"
                            />
                        </Tooltip>
                    </Grid>

                </Grid>
            }
            {/* Location - Only visible if Apollo ELD service is enabled */}
            {apolloEldPermission && (
                <Form.Item
                    ref={registerItem}
                    name="location"
                    label={messages['fleetModalUpdateUnit_location']}
                >
                    <Select
                        id="location"
                        options={locations?.filter((location) => location.active) || []}
                        placeholder={messages['select']}
                        valueField="id"
                        textField="title"
                        style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                        isSearchable
                        isClearable
                        onChange={(value) => {
                            setFormData({
                                ...formData,
                                location: value,
                                locationId: value?.id || 0,
                            });
                        }}
                    />
                </Form.Item>
            )}
            <Form.Item
                ref={registerItem}
                name="timeZone"
                label={messages['fleetModalUpdateUnit_timezone']}
                rules={[
                    { required: true, message: messages["required_field"] },
                ]}
            >
                <Select
                    id="timeZone"
                    options={timeZones || []}
                    placeholder={messages['select']}
                    valueField="id"
                    textField="name"
                    style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                    isSearchable
                    onChange={(value) => {
                        setFormData({
                            ...formData,
                            timeZone: value,
                            timeZoneId: value?.id
                        });
                    }}
                />
            </Form.Item>
            {
                unit?.unitVersion?.manufacturerId === 2 &&
                <div className="form-item" style={{ width: '100%', margin: '0 2px' }}>
                    <span className="input-normal-label">
                        {messages["fleetModalUpdateUnit_consistentSpeed"]}
                    </span>
                    <Slider
                        min={0}
                        max={120}
                        sliderOneColor={false}
                        showValue
                        valueLabelDisplay={"off"}
                        measure="MPH"
                        value={formData.consistentSpeed || 0}
                        onChange={(event) =>
                            setFormData({ ...formData, consistentSpeed: event })
                        }
                    />
                </div>
            }
        </>
    );
}

export default Unit;